import React, { useEffect, useState } from "react";
import { CheckCircle2, Phone, Mail, MapPin, FileSignature } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT, SURF_SERVICES, FISH_SERVICES, SEA_SERVICES, IMAGES } from "../mock";
import Seo from "../components/Seo";

const ALL_SERVICES = [
  ...SURF_SERVICES.map((s) => s.title),
  ...FISH_SERVICES.map((s) => s.title),
  ...SEA_SERVICES.map((s) => s.title),
  "Nicaragua Retreat",
];

const BookToday = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: ALL_SERVICES[0],
    date: "",
    guests: "2",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const payload = {
        ...form,
        _subject: `New Booking Request: ${form.service} — ${form.name}`,
        _template: "table",
        _captcha: "false",
      };
      const res = await fetch("https://formsubmit.co/ajax/hangzensurfschool@gmail.com", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok || data.success === "true" || data.success === true) {
        // Also save locally as backup
        const submissions = JSON.parse(localStorage.getItem("hz_bookings") || "[]");
        submissions.push({ ...form, at: new Date().toISOString() });
        localStorage.setItem("hz_bookings", JSON.stringify(submissions));
        setSent(true);
      } else {
        throw new Error(data.message || "Submission failed");
      }
    } catch (err) {
      setError("Couldn't send right now. Please call (760) 331-3243 or email hangzensurfschool@gmail.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-stone-50">
      <Seo
        title="Book Today | Reserve Your Surf Lesson or Charter"
        description="Reserve your Hang Zen Surf + Sea session. Pay a $50, $100, $250 or $500 deposit via PayPal, or send a booking request. We respond within 24 hours."
        path="/book-today"
      />
      <section className="relative h-[44vh] min-h-[360px] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.hzSunset1})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/30 to-slate-900/70" />
        <div className="relative z-10 h-full flex flex-col justify-end pb-12 max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-semibold mb-3">Reservations</div>
          <h1 className="text-5xl md:text-7xl text-white drop-shadow-2xl" style={{ fontFamily: "'Pacifico', cursive" }}>
            Book Today
          </h1>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            {sent ? (
              <div className="bg-white rounded-2xl p-10 shadow-md text-center">
                <CheckCircle2 className="mx-auto text-green-500 mb-4" size={56} />
                <h2 className="text-3xl text-slate-900 mb-3" style={{ fontFamily: "'Pacifico', cursive" }}>
                  Aloha {form.name.split(" ")[0]}!
                </h2>
                <p className="text-slate-600 mb-6">
                  Your booking request has been received. We'll get back to you within 24 hours to confirm and send a payment link.
                </p>
                <button
                  onClick={() => {
                    setSent(false);
                    setForm({ name: "", email: "", phone: "", service: ALL_SERVICES[0], date: "", guests: "2", message: "" });
                  }}
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full"
                >
                  Book Another
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="bg-white rounded-2xl p-8 shadow-md space-y-5">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Request a Booking</h2>
                <p className="text-slate-600 text-sm mb-4">Fill out the form and we'll confirm availability within 24 hours.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full Name *" name="name" value={form.name} onChange={onChange} required />
                  <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} required />
                  <Field label="Phone *" name="phone" value={form.phone} onChange={onChange} required />
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Service *</label>
                    <select
                      name="service"
                      value={form.service}
                      onChange={onChange}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-400"
                    >
                      {ALL_SERVICES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <Field label="Preferred Date" name="date" type="date" value={form.date} onChange={onChange} />
                  <Field label="# of Guests" name="guests" type="number" value={form.guests} onChange={onChange} />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Message</label>
                  <textarea
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={onChange}
                    placeholder="Skill level, special requests, etc."
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full px-6 py-3.5 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-400 disabled:cursor-not-allowed text-white font-bold rounded-full transition-colors"
                >
                  {sending ? "Sending..." : "Send Request"}
                </button>
                <p className="text-xs text-slate-500 text-center">
                  Prefer to talk? Call {CONTACT.phone} — we usually answer between sets.
                </p>
              </form>
            )}
          </div>

          <aside className="space-y-5">
            <InfoCard icon={Phone} title="Call us" lines={[CONTACT.phone, "Mon–Sat 5:30a–8p"]} href={`tel:${CONTACT.phoneRaw}`} />
            <InfoCard icon={Mail} title="Email" lines={[CONTACT.email]} href={`mailto:${CONTACT.email}`} />
            <InfoCard icon={MapPin} title="Visit us" lines={[CONTACT.address]} href={`https://maps.google.com/?q=${encodeURIComponent(CONTACT.address)}`} />

            <div className="bg-slate-900 text-white rounded-2xl p-6">
              <div className="text-xs uppercase tracking-wider text-orange-400 font-bold mb-2">Pay a Deposit</div>
              <p className="text-slate-300 text-sm mb-4">
                Lock in your spot. Choose a deposit amount and pay securely with PayPal.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {[50, 100, 250, 500].map((amt) => (
                  <a
                    key={amt}
                    href={`${CONTACT.paypal}/${amt}USD`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-1 px-3 py-3 bg-[#0070ba] hover:bg-[#005ea6] text-white font-bold rounded-lg transition-colors"
                  >
                    <span className="text-lg">${amt}</span>
                  </a>
                ))}
              </div>
              <a
                href={CONTACT.paypal}
                target="_blank"
                rel="noreferrer"
                className="block text-center w-full px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold rounded-full"
              >
                Custom amount
              </a>
            </div>
          </aside>
        </div>

        {/* Waiver reminder */}
        <div className="mt-12 md:mt-16 max-w-4xl mx-auto">
          <div
            className="relative overflow-hidden rounded-3xl bg-[#0b3a5f] text-white p-7 md:p-10 shadow-xl"
            data-testid="book-waiver-cta"
          >
            <div
              className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-orange-500/20 blur-2xl pointer-events-none"
              aria-hidden
            />
            <div
              className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-white/5 blur-2xl pointer-events-none"
              aria-hidden
            />
            <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
              <div className="shrink-0 w-14 h-14 rounded-2xl bg-orange-500 text-white flex items-center justify-center shadow-lg">
                <FileSignature size={26} />
              </div>
              <div className="flex-1">
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-300 mb-1.5">
                  Don&apos;t Forget
                </div>
                <h3
                  className="text-2xl md:text-3xl mb-1.5"
                  style={{ fontFamily: "'Pacifico', cursive" }}
                >
                  Sign Your Waiver Before You Surf
                </h3>
                <p className="text-slate-200 text-sm md:text-base max-w-2xl">
                  Every participant must complete a quick liability waiver. Sign
                  digitally in under 60 seconds — parents/legal guardians sign
                  for anyone under 18.
                </p>
              </div>
              <Link
                to="/waiver"
                data-testid="book-waiver-cta-link"
                className="shrink-0 inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full transition-colors text-base whitespace-nowrap"
              >
                <FileSignature size={18} /> Sign Waiver
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, type = "text", value, onChange, required }) => (
  <div>
    <label className="text-sm font-semibold text-slate-700 mb-1.5 block">{label}</label>
    <input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
    />
  </div>
);

const InfoCard = ({ icon: Icon, title, lines, href }) => (
  <a
    href={href}
    target={href?.startsWith("http") ? "_blank" : undefined}
    rel="noreferrer"
    className="flex gap-4 bg-white rounded-2xl p-5 shadow-md hover:shadow-xl transition-shadow"
  >
    <div className="w-11 h-11 shrink-0 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
      <Icon size={20} />
    </div>
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{title}</div>
      {lines.map((l, i) => (
        <div key={i} className="text-slate-800 font-semibold text-sm">{l}</div>
      ))}
    </div>
  </a>
);

export default BookToday;
